import { useEffect } from 'react';

import type { CardOptions } from '@atlaskit/editor-common/card';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import type { ExtractPublicEditorAPI } from '@atlaskit/editor-common/types';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { analyticsPlugin } from '@atlaskit/editor-plugins/analytics';
import { basePlugin } from '@atlaskit/editor-plugins/base';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugins/better-type-history';
import { blockTypePlugin } from '@atlaskit/editor-plugins/block-type';
import { cardPlugin } from '@atlaskit/editor-plugins/card';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugins/clear-marks-on-empty-doc';
import { clipboardPlugin } from '@atlaskit/editor-plugins/clipboard';
import { codeBidiWarningPlugin } from '@atlaskit/editor-plugins/code-bidi-warning';
import { codeBlockPlugin } from '@atlaskit/editor-plugins/code-block';
import { compositionPlugin } from '@atlaskit/editor-plugins/composition';
import { copyButtonPlugin } from '@atlaskit/editor-plugins/copy-button';
import { decorationsPlugin } from '@atlaskit/editor-plugins/decorations';
import { editorDisabledPlugin } from '@atlaskit/editor-plugins/editor-disabled';
import { emojiPlugin } from '@atlaskit/editor-plugins/emoji';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugins/floating-toolbar';
import { focusPlugin } from '@atlaskit/editor-plugins/focus';
import { hyperlinkPlugin } from '@atlaskit/editor-plugins/hyperlink';
import { listPlugin } from '@atlaskit/editor-plugins/list';
import { pastePlugin } from '@atlaskit/editor-plugins/paste';
import { placeholderPlugin } from '@atlaskit/editor-plugins/placeholder';
import { quickInsertPlugin } from '@atlaskit/editor-plugins/quick-insert';
import { rulePlugin } from '@atlaskit/editor-plugins/rule';
import { selectionPlugin } from '@atlaskit/editor-plugins/selection';
import {
	submitEditorPlugin,
	type SubmitEditorPluginOptions,
} from '@atlaskit/editor-plugins/submit-editor';
import { textFormattingPlugin } from '@atlaskit/editor-plugins/text-formatting';
import { typeAheadPlugin } from '@atlaskit/editor-plugins/type-ahead';
import { unsupportedContentPlugin } from '@atlaskit/editor-plugins/unsupported-content';
import { widthPlugin } from '@atlaskit/editor-plugins/width';

import { setAPI } from '../store/SmartCreateStore';

interface PresetOpts {
	placeholderText?: string;
	onSubmit?: SubmitEditorPluginOptions;
}

const cardOptions: CardOptions = {
	allowBlockCards: false,
	allowEmbeds: false,
};

export type SmartPreset = ReturnType<typeof createPreset>;
export type SmartEditorAPI = ExtractPublicEditorAPI<SmartPreset> | undefined;

const createPreset = ({ placeholderText, onSubmit }: PresetOpts) => {
	return (
		new EditorPresetBuilder()
			.add([analyticsPlugin, {}])
			.add(focusPlugin)
			.add(compositionPlugin)
			.add(editorDisabledPlugin)
			.add(basePlugin)
			.add(textFormattingPlugin)
			.add(typeAheadPlugin)
			.add(selectionPlugin)
			.add(listPlugin)
			.add(betterTypeHistoryPlugin)
			.add(quickInsertPlugin)
			.add(emojiPlugin)
			.add(widthPlugin)
			.add(decorationsPlugin)
			.add(blockTypePlugin)
			.add([placeholderPlugin, { placeholder: placeholderText }])
			.add(unsupportedContentPlugin)
			.add(clipboardPlugin)
			.add(codeBidiWarningPlugin)
			.add(clearMarksOnEmptyDocPlugin)
			.add(copyButtonPlugin)
			.add(rulePlugin)
			.add(codeBlockPlugin)
			.add(floatingToolbarPlugin)
			.add([hyperlinkPlugin])
			.maybeAdd([submitEditorPlugin, onSubmit], onSubmit != null)
			// The `cardPlugin` depends on `gridPlugin` which we don't need for comments, since
			// they only use inline links.
			// @ts-expect-error
			.add([cardPlugin, { platform: 'web', ...cardOptions }])
			.add([pastePlugin, { cardOptions }])
	);
};

export const useSmartPreset = (options: PresetOpts): SmartPreset => {
	const { preset, editorApi } = usePreset(() => {
		return createPreset(options);
	}, [options]);

	useEffect(() => {
		if (!setAPI) {
			return;
		}

		setAPI(editorApi);
	}, [editorApi]);

	return preset;
};
